import { useState } from "react";

import { useValidateCustomFormLazyQuery } from "@/apollo/types";
import { Alert, AlertIcon } from "@/components/elements/Alert";
import Select from "@/components/elements/Select";
import { InputDescription } from "@/components/primitives/InputDescription";
import FieldLabel from "@/components/primitives/InputLabel";
import { Input } from "@/components/primitives/input";
import { XIcon } from "lucide-react";

import {
  GoogleAnalytics4Report,
  GoogleAnalytics4ReportNumericOption,
  GoogleAnalytics4ReportOption,
  isAnalyticsReportType,
} from "./GoogleAnalytics4Form";

const INVALID_REPORT_MESSAGE = "Invalid report";
const EXTRA_BACKFILL_OPTIONS: GoogleAnalytics4ReportNumericOption[] =
  Array.from({ length: 30 }, (_, index) => {
    const value = index + 1;
    return { label: String(value), value };
  });

export default function GoogleAnalytics4CustomReport(props: {
  onChangeReport: (report: GoogleAnalytics4Report) => void;
  onRemoveReport: (report: GoogleAnalytics4Report) => void;
  onReportInvalid: (reportId: string) => void;
  onReportValid: (reportId: string) => void;
  report: GoogleAnalytics4Report;
  dateDimensionOptions: { label: string; value: string }[];
  initialDimensionOptions: { label: string; value: string }[];
  initialMetricOptions: { label: string; value: string }[];
  connectionId: string;
  invalidCombination: boolean;
  properties: string[];
  validationError?: string;
}) {
  const {
    report,
    initialDimensionOptions,
    initialMetricOptions,
    onChangeReport,
    onRemoveReport,
    validationError,
    connectionId,
    properties,
    invalidCombination,
    dateDimensionOptions,
    onReportValid,
    onReportInvalid,
  } = props;

  const [validDimensions, setValidDimensions] = useState<
    GoogleAnalytics4ReportOption[] | null
  >(null);
  const [validMetrics, setValidMetrics] = useState<
    GoogleAnalytics4ReportOption[] | null
  >(null);
  const [validationFatalError, setValidationFatalError] =
    useState<boolean>(false);

  const [callValidationQuery, { loading: validationQueryLoading }] =
    useValidateCustomFormLazyQuery();

  const onChange = async (newReport: GoogleAnalytics4Report) => {
    onChangeReport(newReport);
    if (validationFatalError) {
      /*
          Don't call the validation if we got an error before
          The error is either a 5xx error in the call to the API or a rate limiting error
          As Google is sanctioning for producing too many errors (10), we should avoid repeating failing requests
        */
      return;
    }

    if (!newReport.metrics.length || !newReport.dimensions.length) {
      setValidDimensions(null);
      setValidMetrics(null);
      onReportValid(newReport.id); //If we cannot validate, we assume it is valid
      return;
    }

    const queryData = {
      metrics: newReport.metrics.map((x) => x.value),
      dimensions: newReport.dimensions.map((x) => x.value),
      dateDimension: newReport.dateDimension?.value,
      id: newReport.id,
    };

    const { data, error } = await callValidationQuery({
      variables: {
        input: {
          connectionId,
          payload: JSON.stringify({ report: queryData, properties }),
        },
      },
    });

    //If error occurs, show warning and use initial metrics/dimensions
    if (error) {
      if (error.message === INVALID_REPORT_MESSAGE) {
        onReportInvalid(newReport.id);
      } else {
        setValidationFatalError(true);
      }
      setValidDimensions(null);
      setValidMetrics(null);
      return;
    }

    /*
        The result we are getting from the validation endpoint is a list of metrics and dimensions
        that are compatible with the report at it's current state.
      */
    const result = data?.validateCustomForm.customFormValidationResult;
    if (result) {
      onReportValid(newReport.id);
      setValidDimensions(
        result.dimensions.map((x: string) => ({ label: x, value: x })),
      );
      setValidMetrics(
        result.metrics.map((x: string) => ({ label: x, value: x })),
      );
    }
  };

  return (
    <div
      className="relative rounded-xs border bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-700"
      key={report.id}
    >
      <div className="pb-4">
        <FieldLabel id="name" required={true}>
          Name
        </FieldLabel>
        <Input
          value={report.name}
          onChange={(e) => onChange({ ...report, name: e.target.value })}
        />
        {isAnalyticsReportType(report.name) && (
          <div className="text-red-500">
            {report.name} is a reserved report name and cannot be used.
          </div>
        )}
      </div>
      <div className="pb-4">
        <FieldLabel id="name" required={true}>
          Date dimension
        </FieldLabel>
        <Select
          isMulti={false}
          isLoading={validationQueryLoading}
          placeholder="Select one"
          onChange={(v: any) => onChange({ ...report, dateDimension: v })}
          isOptionDisabled={() => report.dimensions?.length >= 8}
          options={dateDimensionOptions}
          value={report.dateDimension} // The legacy option is date, so we set it as default
        />
      </div>
      <div className="pb-4">
        <FieldLabel id="name" required={true}>
          Dimensions in custom report ({report.dimensions?.length ?? 0}/8)
        </FieldLabel>
        <Select
          isMulti
          isLoading={validationQueryLoading}
          placeholder="Select one or more..."
          onChange={(v: any) => onChange({ ...report, dimensions: v })}
          isOptionDisabled={() => report.dimensions?.length >= 8}
          options={validDimensions || initialDimensionOptions}
          value={report.dimensions}
        />
      </div>
      <div className="pb-4">
        <FieldLabel id="name" required={true}>
          Metrics in custom report ({report.metrics?.length ?? 0}/10)
        </FieldLabel>
        <Select
          isMulti
          isLoading={validationQueryLoading}
          placeholder="Select one or more..."
          onChange={(v: any) => onChange({ ...report, metrics: v })}
          options={validMetrics || initialMetricOptions}
          isOptionDisabled={() => report.metrics?.length >= 10}
          value={report.metrics}
        />
      </div>
      <div className="pb-4">
        <FieldLabel id="backfill-days" required={false}>
          Backfill days
        </FieldLabel>
        <InputDescription className="mb-2">
          Each value is a specific day offset from today. Selecting day 2
          backfills only that day, not days 1 and 2. Defaults to day 1, day 7,
          and day 30.
        </InputDescription>
        <Select
          isMulti
          placeholder="Select exact day offsets..."
          onChange={(v: any) => {
            const selectedOptions = (v ??
              []) as GoogleAnalytics4ReportNumericOption[];
            onChange({
              ...report,
              backfillInDays: selectedOptions.filter(
                (option) => option.value > 0 && option.value <= 30,
              ),
            });
          }}
          options={EXTRA_BACKFILL_OPTIONS}
          value={report.backfillInDays}
        />
      </div>
      <div className="absolute top-0 right-0 p-2">
        <button type="button" onClick={() => onRemoveReport(report)}>
          <XIcon className="size-4" />
        </button>
      </div>
      {validationError ? (
        <div className="mt-2">
          <Alert status="error">
            <AlertIcon />
            {validationError}
          </Alert>
        </div>
      ) : null}
      {invalidCombination ? (
        <div className="mt-2">
          <Alert status="error">
            <AlertIcon />
            The chosen metrics and dimensions are not compatible. Please remove
            the item you've added last
          </Alert>
        </div>
      ) : null}
      {validationFatalError ? (
        <div className="mt-2">
          <Alert status="warning">
            <AlertIcon />
            We could not validate your report. Dimension and metric combination
            might be invalid.
          </Alert>
        </div>
      ) : null}
    </div>
  );
}
